/**
 * AgoraManager - Gerenciador de sessões de vídeo Agora RTC
 * 
 * Responsável por:
 * - Inicializar cliente Agora RTC
 * - Gerenciar listeners de eventos (user-joined, user-published, etc)
 * - Criar e publicar streams locais de áudio/vídeo
 * - Gerenciar extensão de Virtual Background (blur, cor, imagem)
 * 
 * @class
 */

import AgoraRTC from 'agora-rtc-sdk-ng';
import VirtualBackgroundExtension from 'agora-extension-virtual-background';
import { EVENTS } from './VideoCallService';
import { toAgoraUid } from './AgoraUid';

const AGORA_API_KEY = '61cf3781778b4a15a3460bd5ab7814bb';

export class AgoraManager {
  constructor(eventHandlerCallback) {
    // Criar cliente Agora RTC com configurações
    this._agoraEngine = AgoraRTC.createClient({
      mode: 'rtc',    // Modo de comunicação em tempo real
      codec: 'vp8'    // Codec de vídeo
    });
    
    this._verifyVirtualExtension();
    this._startListeners();
    this._eventHandlerCallback = eventHandlerCallback;
  }

  /**
   * Verifica compatibilidade e registra extensão de Virtual Background
   * 
   * @private
   * @returns {void}
   */
  _verifyVirtualExtension() {
    const virtualBackgroundExtension = new VirtualBackgroundExtension();
    
    // Verificar se o navegador suporta Virtual Background
    if (!virtualBackgroundExtension.checkCompatibility()) {
      console.error('[AgoraManager] Sem suporte para fundo virtual!');
    }
    
    // Registrar extensão no Agora SDK
    AgoraRTC.registerExtensions([virtualBackgroundExtension]);
    this._virtualBackgroundProcessor = virtualBackgroundExtension;
  }

  /**
   * Configura listeners de eventos do Agora RTC
   * 
   * Eventos monitorados:
   * - user-left: Usuário saiu do canal
   * - user-info-updated: Informações do usuário atualizadas
   * - user-published: Usuário publicou stream (audio/video)
   * - user-joined: Usuário entrou no canal
   * - user-unpublished: Usuário parou de publicar stream
   * - network-quality: Qualidade da conexão
   * 
   * @private
   * @returns {void}
   */
  _startListeners() {
    // Usuário saiu do canal
    this._agoraEngine.on('user-left', async (user, reason) => {
      const remoteUsers = this._agoraEngine.remoteUsers;
      this._eventHandlerCallback(EVENTS.USER_LEFT, {
        user,
        reason,
        remoteUsers
      });
    });
    
    // Informações do usuário atualizadas
    this._agoraEngine.on('user-info-updated', async (user, mediaType) => {
      this._eventHandlerCallback(EVENTS.INFO_UPDATE, { user, mediaType });
    });
    
    // Usuário publicou stream de áudio ou vídeo
    this._agoraEngine.on('user-published', async (user, mediaType) => {
      // Inscrever-se automaticamente no stream do usuário
      await this._agoraEngine.subscribe(user, mediaType);
      this._eventHandlerCallback(EVENTS.USER_PUBLISHED, { user, mediaType });
    });
    
    // Usuário entrou no canal
    this._agoraEngine.on('user-joined', async (user) => {
      this._eventHandlerCallback(EVENTS.USER_JOINED, { user, mediaType: null });
    });
    
    // Usuário parou de publicar stream
    this._agoraEngine.on('user-unpublished', (user) => {
      this._eventHandlerCallback(EVENTS.USER_UNPUBLISHED, { user });
    });
    
    // Qualidade da rede
    this._agoraEngine.on('network-quality', (quality) => {
      const uplinkVideoStats = this._agoraEngine.getLocalVideoStats();
      this._eventHandlerCallback(EVENTS.NETWORK_QUALITY, {
        ...quality,
        ...uplinkVideoStats
      });
    });

    this._agoraEngine.on('connection-state-change', (curState, revState, reason) => {
      this._eventHandlerCallback(EVENTS.CONNECTION_STATE, {
        curState,
        revState,
        reason,
      });
    });
  }

  /**
   * Entra no canal Agora e publica streams locais
   * 
   * @async
   * @param {HTMLElement} appendLocalPlayerContainer - Container pai para adicionar vídeo local
   * @param {HTMLElement} localPlayerContainer - Elemento onde o vídeo local será renderizado
   * @param {Object} channelParameters - Parâmetros do canal (será preenchido com tracks)
   * @param {Object} joinParameters - Parâmetros de entrada
   * @param {string} joinParameters.channelName - Nome do canal
   * @param {string} joinParameters.token - Token de autenticação
   * @param {number} joinParameters.uid - UID do usuário
   * @returns {Promise<number>} UID atribuído pelo Agora
   * 
   * @example
   * const uid = await manager.join(
   *   document.getElementById('local-container'),
   *   document.getElementById('local-player'),
   *   channelParams,
   *   { channelName: 'sala123', token: 'abc', uid: 12345 }
   * );
   */
  async join(
    appendLocalPlayerContainer,
    _localPlayerContainer,
    channelParameters,
    joinParameters
  ) {
    if (!joinParameters.channelName) {
      throw new Error('[AgoraManager] Channel name is required');
    }
    if (!joinParameters.token) {
      throw new Error('[AgoraManager] Token is required');
    }
    if (!joinParameters.uid) {
      throw new Error('[AgoraManager] UID is required');
    }

    const agoraUid = toAgoraUid(joinParameters.uid);
    if (!agoraUid) {
      throw new Error(`[AgoraManager] UID Agora inválido: ${joinParameters.uid}`);
    }

    await this.ensureDisconnected();

    if (!channelParameters.localAudioTrack) {
      try {
        channelParameters.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      } catch (_error) {}
    }
    if (!channelParameters.localVideoTrack) {
      channelParameters.localVideoTrack = await this._createCameraTrack();
    }

    const localRoot = appendLocalPlayerContainer || document.getElementById('local_stream');
    if (channelParameters.localVideoTrack && localRoot) {
      localRoot.innerHTML = '';
      localRoot.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;overflow:hidden;background:#111;';
      await channelParameters.localVideoTrack.play(localRoot, {
        fit: 'cover',
        mirror: true,
      });
    }

    const uid = await this._agoraEngine.join(
      AGORA_API_KEY,
      joinParameters.channelName,
      joinParameters.token,
      agoraUid
    );

    const tracksToPublish = [
      channelParameters.localAudioTrack,
      channelParameters.localVideoTrack
    ].filter(Boolean);

    if (tracksToPublish.length === 0) {
      throw new Error('[AgoraManager] Nenhuma track local para publicar');
    }

    await this._agoraEngine.publish(tracksToPublish);
    
    await this._syncExistingRemoteUsers();

    return uid;
  }

  async ensureDisconnected() {
    const state = this._agoraEngine?.connectionState;
    if (!state || state === 'DISCONNECTED') {
      return;
    }
    try {
      await this._agoraEngine.leave();
    } catch (_error) {}
  }

  getRemoteUsers() {
    return this._agoraEngine?.remoteUsers || [];
  }

  getConnectionState() {
    return this._agoraEngine?.connectionState || 'DISCONNECTED';
  }

  /**
   * Inscreve e notifica sobre usuários remotos já presentes no canal.
   * @private
   */
  async _syncExistingRemoteUsers() {
    const remotes = this._agoraEngine.remoteUsers || [];
    for (const user of remotes) {
      this._eventHandlerCallback(EVENTS.USER_JOINED, { user, mediaType: null });
      if (user.hasAudio) {
        await this.subscribeRemote(user, 'audio');
        this._eventHandlerCallback(EVENTS.USER_PUBLISHED, {
          user,
          mediaType: 'audio'
        });
      }
      if (user.hasVideo) {
        await this.subscribeRemote(user, 'video');
        this._eventHandlerCallback(EVENTS.USER_PUBLISHED, {
          user,
          mediaType: 'video'
        });
      }
    }
  }

  async subscribeRemote(user, mediaType) {
    await this._agoraEngine.subscribe(user, mediaType);
  }

  async _createCameraTrack() {
    let frontCameraId = null;
    try {
      const devices = await AgoraRTC.getCameras();
      const front = devices.find((d) => this._facingFromLabel(d.label) === 'user');
      frontCameraId = front?.deviceId || null;
    } catch (_) {}

    const attempts = [
      frontCameraId ? { cameraId: frontCameraId, encoderConfig: '480p_1' } : null,
      { facingMode: 'user', encoderConfig: '480p_1' },
      { facingMode: 'environment', encoderConfig: '480p_1' },
      { encoderConfig: '480p_1' },
      {}
    ].filter(Boolean);

    for (const config of attempts) {
      try {
        return await AgoraRTC.createCameraVideoTrack(config);
      } catch (_) {}
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: frontCameraId
        ? { deviceId: { exact: frontCameraId } }
        : { facingMode: 'user' },
      audio: false
    });
    const mediaStreamTrack = stream.getVideoTracks()[0];
    if (!mediaStreamTrack) throw new Error('[AgoraManager] Nenhuma track de vídeo disponível');

    return AgoraRTC.createCustomVideoTrack({ mediaStreamTrack });
  }

  /**
   * Alterna a câmera da track local entre frontal ('user') e traseira ('environment').
   * Mantém a mesma track (publicação e desfoque continuam ativos).
   *
   * @async
   * @param {ILocalVideoTrack} videoTrack - Track de vídeo local
   * @param {'user'|'environment'} facingMode - Câmera desejada
   * @returns {Promise<'user'|'environment'>} Lado da câmera aberta
   */
  async switchCamera(videoTrack, facingMode) {
    if (!videoTrack) {
      throw new Error('Nenhuma track de vídeo local');
    }

    const previous = this._getCameraInfo(videoTrack);

    if (typeof videoTrack.setDevice === 'function') {
      try {
        await videoTrack.setDevice({ facingMode });
        if (this._isOnRequestedCamera(videoTrack, facingMode, previous)) return facingMode;
      } catch (_) {}

      const cameraId = await this._findCameraId(videoTrack, facingMode);
      if (cameraId) {
        await videoTrack.setDevice(cameraId);
        if (!this._hasCameraChanged(videoTrack, previous)) {
          throw new Error('A câmera não foi trocada');
        }
        return this._facingFromLabel(this._getCameraInfo(videoTrack).label) || facingMode;
      }
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { exact: facingMode } },
      audio: false
    });
    const mediaStreamTrack = stream.getVideoTracks()[0];
    if (!mediaStreamTrack) {
      stream.getTracks().forEach((track) => track.stop());
      throw new Error('Nenhuma track de vídeo disponível');
    }

    try {
      await videoTrack.replaceTrack(mediaStreamTrack, true);
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      throw error;
    }
    return facingMode;
  }

  detectCameraFacing(videoTrack) {
    const { label, facingMode } = this._getCameraInfo(videoTrack);
    const fromLabel = this._facingFromLabel(label);
    if (fromLabel) return fromLabel;
    return facingMode === 'user' || facingMode === 'environment' ? facingMode : null;
  }

  _facingFromLabel(label) {
    if (/back|rear|environment|traseira/i.test(label || '')) return 'environment';
    if (/front|user|frontal/i.test(label || '')) return 'user';
    return null;
  }

  /**
   * Quantidade de câmeras do aparelho. Deve ser chamado com a permissão de câmera já concedida.
   * @returns {Promise<number>}
   */
  async getCameraCount() {
    try {
      const devices = await AgoraRTC.getCameras(true);
      return devices.length;
    } catch (_) {
      return 0;
    }
  }

  /** @private */
  _getCameraInfo(videoTrack) {
    try {
      const mediaStreamTrack = videoTrack.getMediaStreamTrack();
      return {
        ...(mediaStreamTrack?.getSettings?.() || {}),
        label: mediaStreamTrack?.label || '',
      };
    } catch (_) {
      return {};
    }
  }

  /**
   * Não usa getSettings().facingMode: no WebView do Android ele pode continuar o da câmera original.
   * @private
   */
  _isOnRequestedCamera(videoTrack, facingMode, previous) {
    const fromLabel = this._facingFromLabel(this._getCameraInfo(videoTrack).label);
    if (fromLabel) return fromLabel === facingMode;
    return this._hasCameraChanged(videoTrack, previous);
  }

  _hasCameraChanged(videoTrack, previous) {
    const current = this._getCameraInfo(videoTrack);
    if (current.deviceId && previous.deviceId) return current.deviceId !== previous.deviceId;
    if (current.label && previous.label) return current.label !== previous.label;
    return true;
  }

  /**
   * Procura a câmera pelo rótulo; se não achar, usa qualquer câmera diferente da atual.
   * @private
   */
  async _findCameraId(videoTrack, facingMode) {
    let devices = [];
    try {
      devices = await AgoraRTC.getCameras(true);
    } catch (_) {
      return null;
    }

    const currentId = this._getCameraInfo(videoTrack).deviceId;
    const others = devices.filter((d) => d.deviceId !== currentId);
    const byLabel = others.find((d) => this._facingFromLabel(d.label) === facingMode);
    if (byLabel) return byLabel.deviceId;

    return others[0]?.deviceId || null;
  }

  /**
   * Sai do canal e libera recursos
   * 
   * @async
   * @param {Object} channelParameters - Parâmetros do canal com tracks
   * @param {LocalAudioTrack} channelParameters.localAudioTrack - Track de áudio local
   * @param {LocalVideoTrack} channelParameters.localVideoTrack - Track de vídeo local
   * @returns {Promise<void>}
   * 
   * @example
   * await manager.leave(channelParams);
   */
  async leave(channelParameters) {
    try {
      channelParameters.localAudioTrack?.close();
    } catch (_) {}
    try {
      channelParameters.localVideoTrack?.close();
    } catch (_) {}

    try {
      await this._agoraEngine.leave();
    } catch (_) {}
  }

  /**
   * Obtém instância do processador de Virtual Background
   * 
   * Cria e inicializa o processador caso ainda não exista.
   * Injeta o processador no pipeline de vídeo.
   * 
   * @async
   * @param {Object} channelParameters - Parâmetros do canal
   * @param {LocalVideoTrack} channelParameters.localVideoTrack - Track de vídeo local
   * @returns {Promise<VirtualBackgroundProcessor|null>} Processador ou null se erro
   * 
   * @example
   * const processor = await manager.getVirtualBackgroundProcessorInstance(channelParams);
   * if (processor) {
   *   await processor.setOptions({ type: 'blur', blurDegree: 2 });
   * }
   */
  async getVirtualBackgroundProcessorInstance(channelParameters) {
    // Se processador já existe ou não há vídeo, retornar
    if (!this._virtualBackgroundProcessor || !channelParameters.localVideoTrack) {
      return this._virtualBackgroundProcessor;
    }

    // Verificar se já foi criado um processador
    if (!this._virtualBackgroundProcessor.createProcessor) {
      return this._virtualBackgroundProcessor;
    }

    try {
      // Criar instância do processador
      const processor = this._virtualBackgroundProcessor.createProcessor();
      
      // Inicializar o processador com arquivo Wasm
      await processor.init();
      
      // Injetar extensão no pipeline de processamento de vídeo
      channelParameters.localVideoTrack
        .pipe(processor)
        .pipe(channelParameters.localVideoTrack.processorDestination);
      
      // Armazenar referência do processador criado
      this._virtualBackgroundProcessor = processor;
      
      return processor;
    } catch (e) {
      return null;
    }
  }

  /**
   * Define cor de fundo virtual
   * 
   * @async
   * @param {Object} channelParameters - Parâmetros do canal
   * @returns {Promise<void>}
   * 
   * @example
   * await manager.setBackgroundColor(channelParams);
   */
  async setBackgroundColor(channelParameters) {
    if (channelParameters.localVideoTrack) {
      const vbProcessor = await this.getVirtualBackgroundProcessorInstance(channelParameters);
      
      if (vbProcessor) {
        vbProcessor.setOptions({ type: 'color', color: '#F0DFD0' });
        await vbProcessor.enable();
      }
    }
  }

  /**
   * Ativa blur no fundo do vídeo
   * 
   * @async
   * @param {Object} channelParameters - Parâmetros do canal
   * @returns {Promise<void>}
   * 
   * @example
   * await manager.setBackgroundBlurring(channelParams);
   */
  async setBackgroundBlurring(channelParameters) {
    if (channelParameters.localVideoTrack) {
      const vbProcessor = await this.getVirtualBackgroundProcessorInstance(channelParameters);
      
      if (vbProcessor) {
        // blurDegree: 1 = low, 2 = medium, 3 = high
        vbProcessor.setOptions({ type: 'blur', blurDegree: 2 });
        await vbProcessor.enable();
      }
    }
  }

  /**
   * Refaz o pipeline do desfoque depois de trocar a câmera.
   * O SDK passa a publicar a imagem crua, e o processador não reemite a saída sozinho
   * porque a track de saída dele continua a mesma; desconectar e conectar de novo força a reemissão.
   *
   * @async
   * @param {Object} channelParameters - Parâmetros do canal
   * @returns {Promise<void>}
   */
  async restoreVirtualBackground(channelParameters) {
    const track = channelParameters.localVideoTrack;
    const processor = this._virtualBackgroundProcessor;
    if (!track || !processor || processor.createProcessor) return;

    track.unpipe();
    track.pipe(processor).pipe(track.processorDestination);
    await this.setBackgroundBlurring(channelParameters);
  }

  /**
   * Define imagem de fundo virtual
   * 
   * @async
   * @param {Object} channelParameters - Parâmetros do canal
   * @returns {Promise<void>}
   * 
   * @example
   * await manager.setBackgroundImage(channelParams);
   */
  async setBackgroundImage(channelParameters) {
    const imgElement = document.createElement('img');
    
    imgElement.onload = async () => {
      const vbProcessor = await this.getVirtualBackgroundProcessorInstance(channelParameters);
      
      if (vbProcessor) {
        vbProcessor.setOptions({ type: 'img', source: imgElement });
        await vbProcessor.enable();
      }
    };
    
    // Definir caminho da imagem de fundo
    imgElement.src = './background.jpg';
  }

  /**
   * Desativa fundo virtual (volta ao fundo real)
   * 
   * @async
   * @param {Object} channelParameters - Parâmetros do canal
   * @returns {Promise<void>}
   * 
   * @example
   * await manager.disableBackground(channelParams);
   */
  async disableBackground(channelParameters) {
    const vbProcessor = await this.getVirtualBackgroundProcessorInstance(channelParameters);
    
    if (vbProcessor) {
      vbProcessor.disable();
    }
  }
}
